import React, { useContext, useState, useEffect, useRef } from 'react';
import { PBXCallState } from 'app/store/contexts';
import PBXControlCenter from './PBXControlCenter';
import PBXOutboundCallScreen from './PBXOutboundCallScreen';
import PBXBreakReasonDialog from './PBXBreakReasonDialog';
import { AGENT_STATUS, SUPERVISE_MODE_OPTIONS } from './pbxConstants';

const normalizePhoneNumber = (value) => {
  if (!value) return '';
  let cleaned = value.replace(/[^0-9+*#]/g, '');
  cleaned = cleaned.replace(/\+/g, (match, offset) => offset === 0 ? match : '');

  if (cleaned.startsWith('+92')) {
    cleaned = '0' + cleaned.slice(3);
  } else if (cleaned.startsWith('92')) {
    cleaned = '0' + cleaned.slice(2);
  }

  return cleaned;
};

const isValidPakistaniNumber = (value) => /^0\d{10}$/.test(value || '');

const DIAL_PAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

const styles = {
  wrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '16px 0',
    width: '100%',
    height: '100%',
  },
  floatWrapper: {
    animation: 'phoneFloat 6s ease-in-out infinite',
    height: 'clamp(560px, calc(100vh - 100px), 780px)',
    aspectRatio: '360 / 740',
    maxWidth: '100%',
    display: 'flex',
  },
  frame: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(145deg, #4a4a4f 0%, #1c1c1e 55%, #3a3a3f 100%)',
    borderRadius: '12% / 6%',
    padding: '1.6%',
    boxShadow: `
      0 20px 40px rgba(0,0,0,0.4),
      0 8px 16px rgba(0,0,0,0.25),
      inset 0 1px 1px rgba(255,255,255,0.15),
      inset 0 -1px 1px rgba(0,0,0,0.5)
    `,
    position: 'relative',
    animation: 'phoneSlideIn 0.5s ease-out',
    display: 'flex',
    flexDirection: 'column',
  },
  sideBtnLeft1: {
    position: 'absolute', left: '-3px', top: '15%', width: '3px', height: '4%',
    background: 'linear-gradient(90deg, #1a1a1e, #4a4a4f)', borderRadius: '2px 0 0 2px', pointerEvents: 'none',
  },
  sideBtnLeft2: {
    position: 'absolute', left: '-3px', top: '22%', width: '3px', height: '7%',
    background: 'linear-gradient(90deg, #1a1a1e, #4a4a4f)', borderRadius: '2px 0 0 2px', pointerEvents: 'none',
  },
  sideBtnLeft3: {
    position: 'absolute', left: '-3px', top: '31%', width: '3px', height: '7%',
    background: 'linear-gradient(90deg, #1a1a1e, #4a4a4f)', borderRadius: '2px 0 0 2px', pointerEvents: 'none',
  },
  sideBtnRight: {
    position: 'absolute', right: '-3px', top: '20%', width: '3px', height: '11%',
    background: 'linear-gradient(270deg, #1a1a1e, #4a4a4f)', borderRadius: '0 2px 2px 0', pointerEvents: 'none',
  },
  bezel: {
    background: '#000',
    borderRadius: '11% / 5.5%',
    padding: '0.8%',
    flex: 1,
    display: 'flex',
  },
  screen: {
    background: 'linear-gradient(160deg, #f8f8fc 0%, #eceef3 100%)',
    borderRadius: '10% / 5%',
    padding: '2.5% 5% 4%',
    position: 'relative',
    flex: 1,
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    transition: 'filter 0.2s ease, background 0.3s ease',
  },
  screenDark: {
    background: 'linear-gradient(160deg, #1c1c1e 0%, #0a0a0c 100%)',
  },
  pullZone: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '44px',
    zIndex: 6,
    cursor: 'grab',
    touchAction: 'none',
  },
  screenGlare: {
    position: 'absolute', top: 0, left: 0, right: 0, height: '45%',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)',
    pointerEvents: 'none', borderRadius: '10% 10% 0 0 / 5% 5% 0 0', zIndex: 0,
  },
  statusBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '4px 4% 0', height: '4%', minHeight: '28px',
    position: 'relative', zIndex: 1, flexShrink: 0,
  },
  statusLeft: {
    fontSize: 'clamp(10px, 1.6vh, 12px)', fontWeight: 600, color: '#1a1a1a',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  statusRight: {
    display: 'flex', alignItems: 'center', gap: '5px',
    fontSize: 'clamp(9px, 1.4vh, 11px)', fontWeight: 500,
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
  },
  superviseRow: {
    display: 'flex', justifyContent: 'center', gap: '6px',
    padding: '0 4% 6px', marginTop: '8%', flexShrink: 0, position: 'relative', zIndex: 1,
  },
  supervisePill: {
    flex: 1, maxWidth: '84px',
    padding: '5px 4px', borderRadius: '999px', border: '1px solid #d4d4d9',
    background: 'linear-gradient(180deg, #ffffff 0%, #e4e4e9 100%)',
    color: '#1a1a1a', fontSize: 'clamp(9px, 1.4vh, 11px)', fontWeight: 600,
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    cursor: 'pointer', userSelect: 'none', WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation', transition: 'transform 0.1s ease, background 0.15s ease',
  },
  supervisePillActive: {
    background: 'linear-gradient(180deg, #5ac8fa 0%, #007aff 100%)',
    borderColor: '#007aff', color: '#fff',
  },
  notch: {
    position: 'absolute', top: '1.5%', left: '50%', transform: 'translateX(-50%)',
    width: '28%', height: '4%', minHeight: '22px',
    background: '#000', borderRadius: '999px',
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
    paddingRight: '3%', zIndex: 2,
    boxShadow: 'inset 0 0 3px rgba(0,0,0,0.6)', pointerEvents: 'none',
  },
  cameraDot: {
    width: '8px', height: '8px', borderRadius: '50%',
    background: 'radial-gradient(circle at 30% 30%, #2a3550, #0a1020)',
  },
  numberInput: {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    textAlign: 'center',
    fontWeight: 300,
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    letterSpacing: '0.5px',
    padding: '0 8px',
    WebkitAppearance: 'none',
    cursor: 'text',
  },
  displayWrap: {
    flex: '0 0 auto',
    height: '12%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '4%',
    marginBottom: '2%',
    position: 'relative',
    zIndex: 1,
  },
  hint: {
    color: '#ff3b30',
    fontSize: 'clamp(10px, 1.5vh, 11px)',
    textAlign: 'center',
    marginTop: '-4px',
    marginBottom: '8px',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    animation: 'shake 0.3s ease-in-out',
    position: 'relative',
    zIndex: 1,
    flexShrink: 0,
  },
  pad: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 'clamp(10px, 2vh, 18px)',
    justifyItems: 'center',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
    flex: '0 0 auto',
    width: '100%',
    maxWidth: '280px',
    margin: '0 auto 3%',
    padding: '0 4%',
  },
  key: {
    width: '100%',
    aspectRatio: '1',
    maxWidth: 'clamp(56px, 9vh, 72px)',
    borderRadius: '50%',
    background: 'linear-gradient(180deg, #ffffff 0%, #e4e4e9 100%)',
    border: '1px solid #d4d4d9',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    userSelect: 'none',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 'clamp(20px, 3.5vh, 28px)',
    fontWeight: 400,
    color: '#1a1a1a',
    boxShadow: '0 3px 5px rgba(0,0,0,0.12), inset 0 1px 1px rgba(255,255,255,0.9)',
    transition: 'transform 0.08s ease, background 0.08s ease, box-shadow 0.08s ease',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
  },
  keyPressed: {
    background: 'linear-gradient(180deg, #c4c4c9 0%, #a8a8ad 100%)',
    transform: 'scale(0.93)',
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)',
  },
  actionRow: {
    display: 'grid',
    gridTemplateColumns: '1fr minmax(56px, 72px) 1fr',
    alignItems: 'center',
    gap: '8px',
    position: 'relative',
    zIndex: 1,
    flex: '0 0 auto',
    width: '100%',
    maxWidth: '280px',
    margin: 'auto auto 4%',
  },
  callBtn: {
    width: '100%',
    aspectRatio: '1',
    maxWidth: 'clamp(56px, 9vh, 72px)',
    borderRadius: '50%',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    transition: 'transform 0.12s ease, box-shadow 0.12s ease',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    justifySelf: 'center',
  },
  deleteWrap: { display: 'flex', justifyContent: 'flex-start', paddingLeft: '10px' },
  deleteBtn: {
    background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    userSelect: 'none', transition: 'transform 0.1s ease',
    WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
  },
  homeBar: {
    width: '30%', height: '4px', background: '#1a1a1a',
    borderRadius: '3px', margin: '0 auto', opacity: 0.55,
    position: 'relative', zIndex: 1, flexShrink: 0,
    marginBottom: '4px',
  },
  callWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    margin: '-2.5% -5% -4%',
    background: 'linear-gradient(180deg, #3a3a4a 0%, #1a1a24 100%)',
    position: 'relative',
    zIndex: 1,
    borderRadius: '10% / 5%',
    overflow: 'hidden',
  },
  callHomeBar: {
    width: '30%',
    height: '4px',
    background: '#fff',
    borderRadius: '3px',
    margin: '0 auto 8px',
    opacity: 0.7,
    flexShrink: 0,
  },
};

function PBXDialerDialog({ onUpdateInfoClick, onCreateTicketClick }) {
  const {
    phoneNumber: contextPhoneNumber,
    setPhoneNumber: setContextPhoneNumber,
    makeCall,
    registered,
    accountInfo,
    callState,
    enablePBXCallRingtone,
    handlePBXCallRingtone,
    agentStatus,
    handleAgentStatusChange,
    keypadSound,
    handleKeypadSoundToggle,
    playKeypadTone,
    sipStatus,
    sipConnect,
    sipDisconnect,
    sipBreak,
    canUseSupervise,
    superviseMode,
    openSuperviseAgentPicker,
    closeSuperviseAgentPicker,
  } = useContext(PBXCallState);

  const [localPhoneNumber, setLocalPhoneNumber] = useState(contextPhoneNumber);
  const [pressedKey, setPressedKey] = useState(null);
  const [callHover, setCallHover] = useState(false);
  const inputRef = useRef(null);

  const [ccOpen, setCcOpen] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(null);
  const dragging = useRef(false);
  const dragOffsetRef = useRef(0);

  const [brightness, setBrightness] = useState(100);
  const [theme, setTheme] = useState('light');

  const [breakReasonOpen, setBreakReasonOpen] = useState(false);

  const hasActiveCall = callState === 'incoming' || callState === 'calling' || callState === 'active';

  useEffect(() => { setLocalPhoneNumber(contextPhoneNumber); }, [contextPhoneNumber]);
  useEffect(() => { setContextPhoneNumber(localPhoneNumber); }, [localPhoneNumber, setContextPhoneNumber]);

  const updateNumber = (rawValue) => {
    const normalized = normalizePhoneNumber(rawValue);
    if (!normalized.startsWith('+') && normalized.length > 11) return;
    setLocalPhoneNumber(normalized);
  };

  const handleDialPadPress = (digit) => {
    setPressedKey(digit);
    playKeypadTone?.(digit);
    updateNumber(localPhoneNumber + digit);
  };

  const handleDelete = () => setLocalPhoneNumber(prev => prev.slice(0, -1));
  const handleCallClick = () => { if (canCall) makeCall(); };

  const handleInputChange = (e) => updateNumber(e.target.value);

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    if (pasted) updateNumber(pasted);
  };

  const handlePullPointerDown = (e) => {
    if (ccOpen) return;
    dragging.current = true;
    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragOffsetRef.current = 0;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePullPointerMove = (e) => {
    if (!dragging.current || dragStartY.current === null) return;
    const delta = e.clientY - dragStartY.current;
    const clamped = delta > 0 ? delta : 0;
    dragOffsetRef.current = clamped;
    setDragOffset(clamped);
  };

  const handlePullPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setIsDragging(false);
    if (dragOffsetRef.current > 80) setCcOpen(true);
    setDragOffset(0);
    dragOffsetRef.current = 0;
    dragStartY.current = null;
  };

  const dismissDrag = {
    pointerDown: (e) => {
      dragging.current = true;
      setIsDragging(true);
      dragStartY.current = e.clientY;
      dragOffsetRef.current = 0;
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    pointerMove: (e) => {
      if (!dragging.current || dragStartY.current === null) return;
      const delta = e.clientY - dragStartY.current;
      const clamped = delta < 0 ? delta : 0;
      dragOffsetRef.current = clamped;
      setDragOffset(clamped);
    },
    pointerUp: () => {
      if (!dragging.current) return;
      dragging.current = false;
      setIsDragging(false);
      if (dragOffsetRef.current < -80) setCcOpen(false);
      setDragOffset(0);
      dragOffsetRef.current = 0;
      dragStartY.current = null;
    },
  };

  const handleThemeToggle = () => setTheme(t => t === 'light' ? 'dark' : 'light');

  // Going on break always asks for a reason first; every other status is applied directly.
  const requestAgentStatusChange = (status) => {
    if (status === AGENT_STATUS.BREAK) {
      setBreakReasonOpen(true);
      return;
    }
    handleAgentStatusChange(status);
  };

  const handleBreakReasonConfirm = (reason) => {
    setBreakReasonOpen(false);
    handleAgentStatusChange(AGENT_STATUS.BREAK, reason);
  };

  const isValid = isValidPakistaniNumber(localPhoneNumber);
  const canCall = registered && isValid;

  const isDark = theme === 'dark';
  const textColor = isDark ? '#f2f2f7' : '#1a1a1a';

  return (
    <>
      <style>{`
        @keyframes phoneSlideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes phoneFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes pulseGreen {
          0%, 100% { box-shadow: 0 4px 10px rgba(52,199,89,0.4); }
          50% { box-shadow: 0 4px 18px rgba(52,199,89,0.65); }
        }
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(1.2); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        @keyframes digitPop {
          from { transform: scale(0.7); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .pbx-number-input::placeholder {
          font-size: 13px !important;
          color: #c7c7cc;
          font-weight: 400;
        }
      `}</style>

      <div style={styles.wrapper}>
        <div style={styles.floatWrapper}>
          <div style={styles.frame}>
            <div style={styles.sideBtnLeft1} />
            <div style={styles.sideBtnLeft2} />
            <div style={styles.sideBtnLeft3} />
            <div style={styles.sideBtnRight} />

            <div style={styles.bezel}>
              <div
                style={{
                  ...styles.screen,
                  ...(isDark ? styles.screenDark : {}),
                  filter: `brightness(${Math.max(0.3, brightness / 100)})`,
                }}
              >
                <div
                  style={styles.pullZone}
                  onPointerDown={handlePullPointerDown}
                  onPointerMove={handlePullPointerMove}
                  onPointerUp={handlePullPointerUp}
                  onPointerCancel={handlePullPointerUp}
                />

                <div style={styles.screenGlare} />

                <div style={styles.notch}>
                  <div style={styles.cameraDot} />
                </div>

                {!hasActiveCall && (
                  <div style={styles.statusBar}>
                    <div
                      style={{ ...styles.statusLeft, color: textColor }}
                      title={accountInfo?.displayName || 'Softphone'}
                    >
                      {accountInfo?.displayName || 'Softphone'}
                    </div>
                    <div style={{
                      ...styles.statusRight,
                      color: sipStatus === 'connected' ? '#34c759'
                        : sipStatus === 'break' ? '#ff9500' : '#ff3b30',
                    }}>
                      <span style={{
                        width: '7px', height: '7px', borderRadius: '50%',
                        background: sipStatus === 'connected' ? '#34c759'
                          : sipStatus === 'break' ? '#ff9500' : '#ff3b30',
                        animation: sipStatus !== 'connected' ? 'pulseDot 1.4s ease-in-out infinite' : 'none',
                      }} />
                      {sipStatus === 'connected' ? 'Connected' : sipStatus === 'break' ? 'On Break' : 'Offline'}
                    </div>
                  </div>
                )}

                {!hasActiveCall && canUseSupervise && (
                  <div style={styles.superviseRow}>
                    {SUPERVISE_MODE_OPTIONS.map((opt) => {
                      const isSelected = superviseMode === opt.key;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isSelected) {
                              closeSuperviseAgentPicker();
                            } else {
                              openSuperviseAgentPicker(opt.key);
                            }
                          }}
                          title={`${opt.label} an agent`}
                          style={{
                            ...styles.supervisePill,
                            ...(isSelected ? styles.supervisePillActive : {}),
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {hasActiveCall ? (
                  <div style={styles.callWrapper}>
                    <PBXOutboundCallScreen
                      onUpdateInfoClick={onUpdateInfoClick}
                      onCreateTicketClick={onCreateTicketClick}
                    />
                    <div style={styles.callHomeBar} />
                  </div>
                ) : (
                  <>
                    <div style={styles.displayWrap}>
                      <input
                        ref={inputRef}
                        type="tel"
                        value={localPhoneNumber}
                        onChange={handleInputChange}
                        onPaste={handlePaste}
                        placeholder="Enter number"
                        autoComplete="off"
                        inputMode="tel"
                        className="pbx-number-input"
                        style={{
                          ...styles.numberInput,
                          fontSize: localPhoneNumber.length > 11
                            ? 'clamp(20px, 4.5vh, 26px)'
                            : 'clamp(24px, 5vh, 32px)',
                          color: textColor,
                        }}
                      />
                    </div>

                    {localPhoneNumber && !isValid && (
                      <div style={styles.hint}>Enter a valid 11-digit number</div>
                    )}

                    <div style={styles.pad}>
                      {DIAL_PAD.map((digit) => (
                        <button
                          key={digit}
                          onPointerDown={(e) => {
                            e.preventDefault();
                            handleDialPadPress(digit);
                          }}
                          onPointerUp={() => setPressedKey(null)}
                          onPointerLeave={() => setPressedKey(null)}
                          onPointerCancel={() => setPressedKey(null)}
                          type="button"
                          style={{
                            ...(pressedKey === digit ? { ...styles.key, ...styles.keyPressed } : styles.key),
                            ...(isDark ? {
                              background: pressedKey === digit
                                ? 'linear-gradient(180deg, #3a3a3e 0%, #2a2a2e 100%)'
                                : 'linear-gradient(180deg, #48484a 0%, #2c2c2e 100%)',
                              color: '#f2f2f7',
                              border: '1px solid #2a2a2e',
                            } : {}),
                          }}
                        >
                          {digit}
                        </button>
                      ))}
                    </div>

                    <div style={styles.actionRow}>
                      <div />
                      <button
                        onPointerDown={(e) => {
                          e.preventDefault();
                          handleCallClick();
                        }}
                        onMouseEnter={() => setCallHover(true)}
                        onMouseLeave={() => setCallHover(false)}
                        disabled={!canCall}
                        type="button"
                        style={{
                          ...styles.callBtn,
                          background: canCall
                            ? 'linear-gradient(180deg, #4cd964 0%, #28a745 100%)'
                            : 'linear-gradient(180deg, #c0dcc5 0%, #a8c8ad 100%)',
                          cursor: canCall ? 'pointer' : 'not-allowed',
                          boxShadow: canCall
                            ? '0 4px 10px rgba(52,199,89,0.4), inset 0 1px 1px rgba(255,255,255,0.4)'
                            : 'none',
                          transform: canCall && callHover ? 'scale(1.08)' : 'scale(1)',
                          animation: canCall ? 'pulseGreen 2s ease-in-out infinite' : 'none',
                        }}
                      >
                        <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" style={{ pointerEvents: 'none' }}>
                          <path
                            d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"
                            fill="#fff"
                          />
                        </svg>
                      </button>

                      <div style={styles.deleteWrap}>
                        {localPhoneNumber && (
                          <button
                            onPointerDown={(e) => {
                              e.preventDefault();
                              handleDelete();
                            }}
                            type="button"
                            style={styles.deleteBtn}
                            title="Delete"
                          >
                            <svg width="28" height="20" viewBox="0 0 28 20" fill="none" style={{ pointerEvents: 'none' }}>
                              <path d="M9 2L1.5 10L9 18H25C26.1 18 27 17.1 27 16V4C27 2.9 26.1 2 25 2H9Z"
                                stroke={textColor} strokeWidth="1.5" fill="none" />
                              <path d="M14 7L21 13M21 7L14 13"
                                stroke={textColor} strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    <div style={{ ...styles.homeBar, background: isDark ? '#f2f2f7' : '#1a1a1a' }} />
                  </>
                )}

                <PBXControlCenter
                  isOpen={ccOpen}
                  onClose={() => setCcOpen(false)}
                  dragOffset={dragOffset}
                  isDragging={isDragging}
                  sipStatus={sipStatus}
                  onSipConnect={() => requestAgentStatusChange(AGENT_STATUS.AVAILABLE)}
                  onSipDisconnect={() => requestAgentStatusChange(AGENT_STATUS.OFFLINE)}
                  onSipBreak={() => requestAgentStatusChange(AGENT_STATUS.BREAK)}
                  ringtoneOn={enablePBXCallRingtone}
                  onRingtoneToggle={handlePBXCallRingtone}
                  agentStatus={agentStatus}
                  onAgentStatusChange={requestAgentStatusChange}
                  keypadSound={keypadSound}
                  onKeypadSoundToggle={handleKeypadSoundToggle}
                  theme={theme}
                  onThemeToggle={handleThemeToggle}
                  brightness={brightness}
                  onBrightnessChange={setBrightness}
                  onDismissDrag={dismissDrag}
                />

                <PBXBreakReasonDialog
                  isOpen={breakReasonOpen}
                  onCancel={() => setBreakReasonOpen(false)}
                  onConfirm={handleBreakReasonConfirm}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PBXDialerDialog;